import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  CoreDataEntity,
  DataService,
  ObjectKey,
  PersistMode,
  PersistObjectModel,
  PersistObjectsModel,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
  TargetColumnValue,
  TargetObjectData,
} from '@barthauer/abc/core/data';
import {
  AbcPopupComponent,
  AbcTextBoxComponent,
  _AbcButtonComponent,
  _AbcScrollViewComponent,
  _AbcTagBoxComponent,
} from '@barthauer/abc/ui-components';
import { DxHtmlEditorComponent, DxHtmlEditorModule } from 'devextreme-angular';
import DataSource from 'devextreme/data/data_source';
import { Observable, of, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { Note } from '../../models/note.model';

export interface Labels {
  Title?: string;
  AddNote?: string;
  NoteTitle?: string;
  Delete?: string;
  Edit?: string;
  ConfirmDelete?: string;
  DeleteMsg?: string;
  Cancel?: string;
  NoData?: string;
  NoteFrom?: string;
  Previous?: string;
  Next?: string;
  Save?: string;
}

@Component({
  selector: 'abc-common-editors-notes',
  templateUrl: './notes.component.html',
  styleUrls: ['./notes.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    FlexLayoutModule,
    _AbcScrollViewComponent,
    _AbcButtonComponent,
    AbcPopupComponent,
    DxHtmlEditorModule,
    AbcTextBoxComponent,
    _AbcTagBoxComponent,
  ],
})
export class NotesComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('addNotePopup') addNotePopup: AbcPopupComponent;
  @ViewChild('deletePopup') deletePopup: AbcPopupComponent;
  @ViewChild('htmlEditor') htmlEditor: DxHtmlEditorComponent;

  @Input() parentObjects: CoreDataEntity[] = [];
  @Input() opath: string;
  @Input() personsOpath: string;
  @Input() height = '100%';
  @Input() userName = sessionStorage.getItem('userName');
  @Input() enablePersonsSelection = true;
  @Input() enableAdd = true;
  @Input() enableUpdate = true;
  @Input() disableDelete = false;
  @Input() InsertedById = sessionStorage.getItem('profileId');
  @Input() labels: Labels = defaultNoteLabels

  private commentObjectType = 'Comment';
  private personObjectType = 'Person';
  private reminderObjectType = 'Reminder';
  public notes: Note[] = [];
  public currentNote: Note;
  public currentNoteIndex: number = -1;
  public currentNoteIndexSubject: Subject<number> = new Subject();
  public noteDescription: string;
  public noteTitle: string;
  public toUpdate = false;
  public isPersisting = false;
  public personsIds: string[] = [];
  public personsIdsToBeAdded: string[] = [];
  public personsIdsToBeDeleted: string[] = [];
  public persons: DataSource;

  private destroySubject: Subject<void> = new Subject();

  constructor(private coreDataService: DataService, private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    this.currentNoteIndexSubject.pipe(takeUntil(this.destroySubject)).subscribe((index) => {
      this.currentNoteIndex = index;
      if (index === -1) {
        this.currentNote = new Note(null);
      } else {
        this.currentNote = this.notes[index];
      }

      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.opath && changes.opath?.currentValue?.length > 0) {
      this.prepareData(changes.opath.currentValue);
    }

    if (changes.personsOpath && changes.personsOpath?.currentValue?.length > 0) {
      this.preparePersonsData(changes.personsOpath.currentValue);
    }
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  public onAddPopupShown(e): void {
    e.component.content().parentElement.parentElement.classList.add('abc-cen-add-popup');
  }
  public onDeletePopupShown(e): void {
    e.component.content().parentElement.parentElement.classList.add('abc-cen-delete-popup');
  }

  private prepareData(opath: string): void {
    this.getNotes(opath)
      .pipe(takeUntil(this.destroySubject))
      .subscribe((notes) => {
        if (Array.isArray(notes) && notes.length > 0) {
          this.notes = notes;
          this.currentNoteIndexSubject.next(0);
        } else {
          this.notes = [];
        }
        this.cdr.markForCheck();
      });
  }
  public getNotes(opath: string): Observable<any> {
    const commentColumns: QueryColumn[] = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('Comment', 'Comment', undefined, undefined, undefined, undefined, 'Html'),
      this.coreDataService.createQueryColumn('Text', 'Text'),
      this.coreDataService.createQueryColumn('ParentId', 'ParentId'),
      this.coreDataService.createQueryColumn('ParentObjectType', 'ParentObjectType'),
      this.coreDataService.createQueryColumn(
        "FormatDate(SysDateInsert,'dd.MM.yyyy')",
        'DateInsert',
        QueryColumnSortOrder.Descending
      ),
      this.coreDataService.createQueryColumn(
        "FormatTime(SysTimeInsert, 'HH:mm')",
        'SysTimeInsert',
        QueryColumnSortOrder.Descending
      ),
      this.coreDataService.createQueryColumn('SysUserNameInsert', 'SysUserNameInsert'),
      this.coreDataService.createQueryColumn('IdRefPersonInsert', 'IdRefPersonInsert'),
    ];

    const readObjectQuery: QueryObjectsModel = new QueryObjectsModel();
    const commentQuery: Query = new Query();
    commentQuery.ObjectType = this.commentObjectType;
    commentQuery.Columns = commentColumns;
    commentQuery.OPath = opath;

    const reminderQuery: Query = new Query();
    reminderQuery.Name = 'Reminders';
    reminderQuery.OPath = 'Reminders';
    const reminderQueryColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('IdRefPersonFor', 'IdRefPersonFor'),
    ];
    reminderQuery.Columns = reminderQueryColumns;

    commentQuery.ObjectQueries = [reminderQuery];

    readObjectQuery.ObjectQueries = [commentQuery];
    return this.coreDataService.executeReadObjectsQuery(readObjectQuery);
  }

  private preparePersonsData(opath: string): void {
    this.getPersons(opath)
      .pipe(takeUntil(this.destroySubject))
      .subscribe((persons) => {
        if (Array.isArray(persons) && persons.length > 0) {
          this.persons = new DataSource({
            store: persons.sort((a, b) => a.LastName.localeCompare(b.LastName)),
            paginate: true,
          });
        }
        this.cdr.markForCheck();
      });
  }

  public getPersons(opath: string): Observable<any> {
    const personColumns: QueryColumn[] = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('FirstName', 'FirstName'),
      this.coreDataService.createQueryColumn('LastName', 'LastName'),
      this.coreDataService.createQueryColumn('ref:Id', 'FullName'),
    ];

    const mainQuery: Query = new Query();
    mainQuery.ObjectType = this.personObjectType;
    mainQuery.Columns = personColumns;
    mainQuery.OPath = opath;

    const readObjectQuery: QueryObjectsModel = new QueryObjectsModel();
    readObjectQuery.ObjectQueries = [mainQuery];
    return this.coreDataService.executeReadObjectsQuery(readObjectQuery);
  }
  public openAddPopup(): void {
    this.htmlEditor.instance.reset();
    this.addNotePopup.instance.show();
  }
  public openUpdatePopup(): void {
    this.noteTitle = this.currentNote.Text;
    this.noteDescription = this.currentNote.Comment;
    this.personsIds =
      this.currentNote.Reminders && this.currentNote.Reminders.length > 0
        ? this.currentNote.Reminders.map((reminder) => reminder.IdRefPersonFor)
        : [];
    this.toUpdate = true;
    this.addNotePopup.instance.show();
  }
  public persistNote(): void {
    this.isPersisting = true;
    for (const parentObject of this.parentObjects) {
      let noteToPersist = new Note({
        Text: this.noteTitle,
        Comment: this.noteDescription,
        ParentId: parentObject.id,
        ParentObjectType: parentObject.objectType,
      });

      if (this.toUpdate) {
        noteToPersist.Id = this.currentNote.Id;
      }

      const request = this.toUpdate
        ? this.persistNoteService(noteToPersist).pipe(
            switchMap((note) => {
              const personsIdToAdd = this.personsIdsToBeAdded.filter((personId) => {
                if (this.currentNote.Reminders && this.currentNote.Reminders.length > 0) {
                  return !this.currentNote.Reminders.some((reminder) => reminder.IdRefPersonFor === personId);
                } else {
                  return true;
                }
              });
              const personsToDelete =
                this.currentNote.Reminders && this.currentNote.Reminders.length > 0
                  ? this.currentNote.Reminders.filter((reminder) => {
                      if (this.personsIdsToBeDeleted && this.personsIdsToBeDeleted.length > 0) {
                        return this.personsIdsToBeDeleted.some((personId) => personId === reminder.IdRefPersonFor);
                      } else {
                        return false;
                      }
                    }).map((reminder) => reminder.Id)
                  : [];

              return this.persistReminderService(note.Id, personsIdToAdd, personsToDelete).pipe(
                map((reminders) => note)
              );
            })
          )
        : this.persistNoteService(noteToPersist).pipe(
            switchMap((note) => {
              const personsIdToAdd = this.enablePersonsSelection ? this.personsIds : [this.InsertedById];
              return this.persistReminderService(note.Id, personsIdToAdd).pipe(map((reminders) => note));
            })
          );

      request.pipe(takeUntil(this.destroySubject)).subscribe(
        (note) => {
          this.getNotes(this.opath)
            .pipe(takeUntil(this.destroySubject))
            .subscribe(
              (notes) => {
                if (Array.isArray(notes) && notes.length > 0) {
                  this.notes = notes;
                  this.currentNote = notes[0];
                }
                this.closePersistPopup();
                this.isPersisting = false;
                this.currentNoteIndexSubject.next(0);
                this.cdr.markForCheck();
              },
              (error) => {
                this.closePersistPopup();
                this.isPersisting = false;
              }
            );
        },
        (error) => {
          console.error(error);
          this.closePersistPopup();
          this.isPersisting = false;
        }
      );
    }
  }
  private persistNoteService(note: Note): Observable<any> {
    const commentPersistQuery: TargetObjectData = new TargetObjectData();
    commentPersistQuery.Mode = note.Id ? PersistMode.Update : PersistMode.Insert;

    commentPersistQuery.ObjectKey = new ObjectKey();
    commentPersistQuery.ObjectKey.ObjectType = this.commentObjectType;

    if (note.Id) commentPersistQuery.ObjectKey.Id = note.Id;

    const commentColumns: TargetColumnValue[] = [
      { Name: 'Text', Value: note.Text },
      { Name: 'Comment', Value: note.Comment },
      { Name: 'ParentObjectType', Value: note.ParentObjectType },
      { Name: 'ParentId', Value: note.ParentId },
      { Name: 'IdRefPersonInsert', Value: this.InsertedById },
    ];

    commentPersistQuery.TargetColumns = commentColumns;
    const commentQuery: PersistObjectModel = new PersistObjectModel();
    commentQuery.Object = commentPersistQuery;
    return this.coreDataService.executePersistObjectQuery(commentQuery);
  }
  private persistReminderService(noteId: string, personsToAdd: string[], personsToDelete?: string[]): Observable<any> {
    const query: PersistObjectsModel = new PersistObjectsModel();
    const objectsPersistQuery: TargetObjectData[][] = [[]];
    objectsPersistQuery[1] = [];

    if (personsToAdd && personsToAdd.length > 0) {
      personsToAdd?.forEach((personId) => {
        const objectData: TargetObjectData = new TargetObjectData();
        objectData.Mode = PersistMode.Insert;
        objectData.ObjectKey = new ObjectKey();
        objectData.ObjectKey.ObjectType = this.reminderObjectType;
        const queryColumns: TargetColumnValue[] = [
          { Name: 'IdRefComment', Value: noteId },
          { Name: 'IdRefPersonFor', Value: personId },
          { Name: 'ParentId', Value: noteId },
          { Name: 'ParentObjectType', Value: this.commentObjectType },
          { Name: 'IsActive', Value: true },
          { Name: 'Date', Value: new Date() },
        ];
        objectData.TargetColumns = queryColumns;
        objectsPersistQuery[0].push(objectData);
      });
    }

    //reminders Ids
    if (personsToDelete && personsToDelete.length > 0) {
      personsToDelete?.forEach((reminderId) => {
        const objectData: TargetObjectData = new TargetObjectData();
        objectData.Mode = PersistMode.Delete;
        objectData.ObjectKey = new ObjectKey();
        objectData.ObjectKey.ObjectType = this.reminderObjectType;
        objectData.ObjectKey.Id = reminderId;
        objectsPersistQuery[1].push(objectData);
      });
    }
    query.Objects = objectsPersistQuery;
    return this.coreDataService.executePersistObjectsQuery(query);
  }
  public openDeletePopup(): void {
    this.deletePopup.instance.show();
  }

  public persistDelete(): void {
    if (this.currentNote.Id) {
      this.getReminders(this.currentNote.Id)
        .pipe(
          switchMap((remindersIds) => {
            remindersIds =
              remindersIds && remindersIds.length > 0 ? remindersIds.map((reminderId) => reminderId.Id) : [];
            return this.deleteReminderService(remindersIds).pipe(
              switchMap((deletedReminders) => this.deleteNoteService(this.currentNote.Id))
            );
          })
        )
        .pipe(takeUntil(this.destroySubject))
        .subscribe(
          (deleted) => {
            const index = this.notes.findIndex((item) => item.Id === this.currentNote.Id);
            this.notes.splice(index, 1);
            if (this.notes.length > 0) {
              if (index === 0) {
                this.currentNoteIndexSubject.next(0);
              } else {
                this.currentNoteIndexSubject.next(index - 1);
              }
            } else {
              this.currentNoteIndexSubject.next(-1);
            }
            this.deletePopup.instance.hide();
          },
          (error) => {
            console.error(error);
          }
        );
    } else {
      this.deletePopup.instance.hide();
    }
  }

  public personValueChanged(event): void {
    this.personsArrayUpdate(event.addedItems, this.personsIdsToBeAdded, this.personsIdsToBeDeleted);

    this.personsArrayUpdate(event.removedItems, this.personsIdsToBeDeleted, this.personsIdsToBeAdded);
  }

  private personsArrayUpdate(dataArray: any[], arrayToAddTo: string[], arrayToRemoveFrom: string[]): void {
    if (dataArray && dataArray.length > 0) {
      dataArray.forEach((item) => {
        if (!arrayToAddTo.includes(item.Id)) {
          arrayToAddTo.push(item.Id);
        }
        const index = arrayToRemoveFrom.indexOf(item.Id);
        if (index > -1) {
          arrayToRemoveFrom.splice(index, 1);
        }
      });
    }
  }
  private getReminders(noteId: string): Observable<any> {
    const remindersColumns: QueryColumn[] = [this.coreDataService.createQueryColumn('Id', 'Id')];
    const readObjectQuery: QueryObjectsModel = new QueryObjectsModel();
    const mainQuery: Query = new Query();
    mainQuery.ObjectType = this.reminderObjectType;
    mainQuery.Columns = remindersColumns;
    mainQuery.OPath = `ParentId ='${noteId}'`;
    readObjectQuery.ObjectQueries = [mainQuery];
    return this.coreDataService.executeReadObjectsQuery(readObjectQuery);
  }

  private deleteReminderService(reminderIds: string[]): Observable<any> {
    if (reminderIds && reminderIds.length > 0) {
      const query: PersistObjectsModel = new PersistObjectsModel();
      const objectsPersistQuery: TargetObjectData[][] = [[]];
      reminderIds?.forEach((reminderId) => {
        const objectData: TargetObjectData = new TargetObjectData();
        objectData.Mode = PersistMode.Delete;
        objectData.ObjectKey = new ObjectKey();
        objectData.ObjectKey.ObjectType = this.reminderObjectType;
        objectData.ObjectKey.Id = reminderId;
        objectsPersistQuery[0].push(objectData);
      });
      query.Objects = objectsPersistQuery;
      return this.coreDataService.executePersistObjectsQuery(query);
    } else {
      return of([]);
    }
  }
  public deleteNoteService(id: string): Observable<any> {
    const persistQuery: PersistObjectModel = new PersistObjectModel();

    const targetObject: TargetObjectData = new TargetObjectData();
    targetObject.Mode = PersistMode.Delete;
    targetObject.ObjectKey = new ObjectKey();
    targetObject.ObjectKey.Id = id;
    targetObject.ObjectKey.ObjectType = this.commentObjectType;

    persistQuery.Object = targetObject;
    return this.coreDataService.executePersistObjectQuery(persistQuery);
  }
  public closeDeletePopup() {
    this.deletePopup.instance.hide();
  }
  public closePersistPopup = () => {
    this.htmlEditor.instance.clearHistory()
    this.toUpdate = false;
    this.noteTitle = '';
    this.noteDescription = '';
    this.personsIds = [];
    this.htmlEditor.instance.reset();
    this.addNotePopup.instance.hide();
  };
}
export const  defaultNoteLabels:Labels={

    Title: 'Notizen',
    AddNote: 'Notiz hinzufügen',
    NoteTitle: 'Titel',
    Delete: 'Löschen',
    Edit: 'Bearbeiten',
    NoData: 'Keine Daten..',
    NoteFrom: 'Notiz von',
    Previous: 'Vorherige',
    Next: 'Weiter',
    ConfirmDelete: 'Löschen bestätigen',
    DeleteMsg: 'Sind Sie sicher, dass sie diese Notiz löschen möchten?',
    Save: 'Speichern',
    Cancel: 'Abbrechen',
  
}